import React, { useMemo, useRef, useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { X } from 'lucide-react';
import { useBrands } from '@/hooks/useBrands';
import { toast } from "@/hooks/use-toast";
import { useOrganization } from '@/contexts/OrganizationContext';
import { useAuth } from '@/contexts/AuthContext';
import { useEnqueueEnrichment } from '@/hooks/useEnqueueEnrichment';
import { CampaignFormStep1 } from './campaign-creation/CampaignFormStep1';
import { CampaignFormStep2 } from './campaign-creation/CampaignFormStep2';
import { createCampaignWithInfluencers } from '@/hooks/useCampaignCreation';
import { useUsageLimits } from '@/hooks/useBillingAndUsage';
import { limitExceededMessage, toFriendlyLimitError } from '@/lib/limitErrors';
import { cleanHandles } from '@/lib/utils/csvParsing';
import type { CampaignCreationFormProps, CampaignFormData } from './campaign-creation/campaignFormTypes';
import AddBrandModal from './AddBrandModal';

const CampaignCreationForm = ({ onClose, onSubmit, prefilledData }: CampaignCreationFormProps) => {
  const { currentOrganization } = useOrganization();
  const { userCountry } = useAuth();
  const { data: brands, isLoading: brandsLoading } = useBrands();
  const { enqueueJob } = useEnqueueEnrichment();
  const { isAtLimit } = useUsageLimits();

  const [currentStep, setCurrentStep] = useState(1);
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [endDateTouched, setEndDateTouched] = useState(false);
  const [selectedCreatorLists, setSelectedCreatorLists] = useState<string[]>([]);
  const [creatorUsernames, setCreatorUsernames] = useState('');
  const [csvHandles, setCsvHandles] = useState<string[]>([]);
  const [csvFileName, setCsvFileName] = useState<string | null>(null);
  const [isAddBrandOpen, setIsAddBrandOpen] = useState(false);
  const submitInFlightRef = useRef(false);

  const today = new Date().toISOString().split("T")[0];

  const [formData, setFormData] = useState<CampaignFormData>({
    name: '',
    brandId: prefilledData?.brandId || '',
    hashtags: '',
    upgradeAlerts: false,
    flagMissingAdDisclosure: false,
    enableEventMode: false,
    campaignType: 'awareness',
    platforms: [],
    totalBudget: '',
    startDate: today,
    endDate: ''
  });

  // Auto-select brand if logged in as a brand — runs whenever brands load or org changes
  useEffect(() => {
    if (currentOrganization?.organization_type === 'brand' && brands && brands.length > 0 && !formData.brandId) {
      const defaultBrand = brands[0];
      setFormData(prev => ({ ...prev, brandId: defaultBrand.id }));
    }
  }, [currentOrganization, brands]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!formData.startDate || endDateTouched) {
      return;
    }

    const nextMonth = new Date(formData.startDate);
    nextMonth.setMonth(nextMonth.getMonth() + 1);
    setFormData(prev => ({ ...prev, endDate: nextMonth.toISOString().split("T")[0] }));
  }, [formData.startDate, endDateTouched]);

  const updateFormData = <Field extends keyof CampaignFormData>(
    field: Field,
    value: CampaignFormData[Field]
  ) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const manualHandles = useMemo(() => cleanHandles(creatorUsernames), [creatorUsernames]);
  const mergedHandles = useMemo(
    () => Array.from(new Set([...manualHandles, ...csvHandles])),
    [manualHandles, csvHandles]
  );

  if (!currentOrganization) {
    return null;
  }

  const isBrandOrg = currentOrganization.organization_type === 'brand';

  const validateStepOne = () => {
    if (!currentOrganization?.id) {
      toast({
        title: "No organization selected",
        description: "Please select an organization first.",
        variant: "destructive",
      });
      return false;
    }

    if (!formData.name) {
      setCreateError("Please enter a campaign name");
      return false;
    }

    if (!isBrandOrg && !formData.brandId) {
      setCreateError("Please select a brand");
      return false;
    }

    setCreateError(null);
    return true;
  };

  const handleNext = () => {
    if (!validateStepOne()) {
      return;
    }
    setCurrentStep(2);
  };

  const handleCreatorListSelect = (listId: string) => {
    if (selectedCreatorLists.includes(listId)) {
      return;
    }
    setSelectedCreatorLists(prev => [...prev, listId]);
  };

  const removeCreatorList = (listId: string) => {
    setSelectedCreatorLists(prev => prev.filter(id => id !== listId));
  };

  const removeHandle = (handle: string) => {
    setCreatorUsernames(prev => cleanHandles(prev).filter(item => item !== handle).join('\n'));
    setCsvHandles(prev => prev.filter(item => item !== handle));
  };

  const handleCsvHandlesParsed = (handles: string[], fileName: string) => {
    setCsvFileName(fileName);
    setCsvHandles(prev => Array.from(new Set([...prev, ...handles])));
  };

  const handleFinalSubmit = async () => {
    if (submitInFlightRef.current) {
      return;
    }

    if (!validateStepOne()) {
      return;
    }

    if (mergedHandles.length === 0) {
      const message = "Add at least one creator username before starting the campaign.";
      setCreateError(message);
      toast({
        title: "No creators added",
        description: message,
        variant: "destructive",
      });
      return;
    }

    if (isAtLimit('campaigns')) {
      const message = limitExceededMessage('campaigns');
      setCreateError(message);
      toast({
        title: "Campaign limit reached",
        description: message,
        variant: "destructive",
      });
      return;
    }

    submitInFlightRef.current = true;
    setCreateLoading(true);
    setCreateError(null);

    try {
      const data = await createCampaignWithInfluencers({
        formData,
        mergedHandles,
        organizationId: currentOrganization.id,
        isBrandOrg,
        brands,
        enqueueJob,
        userCountry,
      });

      if (data && onSubmit) {
        onSubmit(data);
      }
    } catch (err: unknown) {
      const fallback =
        err instanceof Error
          ? err.message
          : "We couldn't create the campaign right now. Please try again.";
      const message = toFriendlyLimitError(err, fallback);
      setCreateError(message);
      toast({
        title: "Error",
        description: message,
        variant: "destructive",
      });
    } finally {
      submitInFlightRef.current = false;
      setCreateLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="w-full max-w-4xl max-h-[90vh] overflow-y-auto mx-auto bg-white rounded-lg p-8 relative">
        <AddBrandModal
          open={isAddBrandOpen}
          setOpen={setIsAddBrandOpen}
          existingBrands={brands || []}
          onSuccess={(newBrand) => {
            setFormData((prev) => ({ ...prev, brandId: newBrand.id }));
          }}
        />
        <Button variant="ghost" size="icon" onClick={onClose} className="absolute top-6 right-6 text-gray-400 hover:text-gray-600">
          <X className="w-5 h-5" />
        </Button>
        <h1 className="text-2xl font-bold text-gray-900 mb-8">
          {currentStep === 1 ? 'Create Campaign' : 'Select creators'}
        </h1>

        {currentStep === 1 && (
          <CampaignFormStep1
            formData={formData}
            isBrandOrg={isBrandOrg}
            brands={brands}
            brandsLoading={brandsLoading}
            currentOrgName={currentOrganization?.name}
            createLoading={createLoading}
            createError={createError}
            userCountry={userCountry}
            onUpdateFormData={updateFormData}
            onEndDateTouched={() => setEndDateTouched(true)}
            onNext={handleNext}
            onClose={onClose}
            onAddBrandClick={() => setIsAddBrandOpen(true)}
          />
        )}

        {currentStep === 2 && (
          <CampaignFormStep2
            creatorUsernames={creatorUsernames}
            mergedHandles={mergedHandles}
            csvFileName={csvFileName}
            selectedCreatorLists={selectedCreatorLists}
            createLoading={createLoading}
            createError={createError}
            onSetCreatorUsernames={setCreatorUsernames}
            onCsvHandlesParsed={handleCsvHandlesParsed}
            onRemoveHandle={removeHandle}
            onCreatorListSelect={handleCreatorListSelect}
            onRemoveCreatorList={removeCreatorList}
            onSubmit={handleFinalSubmit}
            onBack={() => setCurrentStep(1)}
            onClose={onClose}
          />
        )}
      </div>
    </div>
  );
};

export default CampaignCreationForm;
